'use client'

import { JSX } from 'react'
import { useRouter } from 'next/navigation'
import { ChevronRight } from 'lucide-react'
import { Locale } from '@/providers/Locale/types'
import { Taboola } from '@/components/frontend/Taboola'

import { ArticleInList } from '@/components/frontend/Layouts/ArticleInList'
import { OneColList, type Props as OneColListProps } from '@/components/frontend/Layouts/OneColList'
import { type PartialPost } from '@/utilities/postToArticle'
import { ClassValue } from 'clsx'
import { cn } from '@/utilities/ui'

type Props = {
    posts: Array<PartialPost | string> | undefined
    nextButtonText: string
    hasNextPage: boolean
    nextPage?: number
    locale: Locale
    options?: OneColListProps
    classes?: {
        nextButton: ClassValue
    }
}

export function PostList({ posts, hasNextPage, nextPage, locale, nextButtonText, options, classes }: Props): JSX.Element {
    const router = useRouter()

    return (
        <>
            <OneColList hasRightRail classes={{ container: 'pt-10', main: 'p-10 pr-[18px] *:py-8' }} {...options}>
                {posts?.map((post, i) => {
                    if (typeof post === 'string') {
                        return null
                    }

                    if (i < posts.length - 1) {
                        return (
                            <ArticleInList
                                key={post.id}
                                post={post}
                                variant='large'
                                media={{
                                    media: post.heroImage,
                                    options: {
                                        useHoverImageOverlay: true,
                                        className: 'aspect-square',
                                    },
                                }}
                                locale={locale}
                                showPublishTimeOnly
                                showBreadcrumbs
                            />
                        )
                    }

                    return (
                        <div key={`wrapper-${i}`}>
                            <ArticleInList
                                post={post}
                                variant='large'
                                media={{
                                    media: post.heroImage,
                                    options: {
                                        useHoverImageOverlay: true,
                                        className: 'aspect-square',
                                    },
                                }}
                                locale={locale}
                                showPublishTimeOnly
                                showBreadcrumbs
                            />
                            {hasNextPage && nextPage ? (
                                <div className='w-full flex justify-center mt-5 mb-10 pt-8'>
                                    <button
                                        aria-label='Next page'
                                        onClick={() => {
                                            router.push(`?page=${nextPage}`)
                                        }}
                                        className={cn(
                                            'px-8 py-2 bg-white text-stone-500 hover:text-zinc-800 transition-all duration-200 border border-zinc-800',
                                            classes?.nextButton,
                                        )}
                                    >
                                        {nextButtonText} <ChevronRight className='inline-block stroke-1' />
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    )
                })}
                <Taboola placement='Below Section Fronts Thumbnails' />
            </OneColList>
        </>
    )
}
